import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseButton, NumberInput, InputProps } from '@app/shared/ui/atoms/Common';
import { cn } from '@app/shared/utils/common';

interface DateOfBirthInputProps {
  day: string;
  month: string;
  year: string;
  variant?: InputProps['variant'];
  align?: 'left' | 'center';
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => boolean;
  onClear: () => void;
}

export function DateOfBirthInput({
  day,
  month,
  year,
  variant,
  align = 'left',
  onChange,
  onClear,
}: DateOfBirthInputProps) {
  const { t } = useTranslation();

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const moveFocus = (nextRef: React.RefObject<HTMLInputElement | null>) => {
    nextRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const changed = onChange(e);

    if (!changed) {
      return;
    }

    const { name, value } = e.target;
    const num = Number(value);

    if (name === 'day' && num > 3) {
      moveFocus(monthRef);
    }
    if (name === 'month' && num >= 1) {
      moveFocus(yearRef);
    }
  };

  const config = {
    variant,
    className: cn(align === 'center' && 'text-center'),
    onChange: handleChange,
  } as const;

  return (
    <div className="grid grid-cols-[1fr_1fr_1.375fr_auto] items-center gap-2">
      <NumberInput
        ref={dayRef}
        value={day}
        name="day"
        min={1}
        max={31}
        placeholder={t('common.day')}
        {...config}
      />
      <NumberInput
        ref={monthRef}
        value={month}
        name="month"
        min={1}
        max={12}
        placeholder={t('common.month')}
        {...config}
      />
      <NumberInput
        ref={yearRef}
        value={year}
        name="year"
        min={1900}
        max={new Date().getFullYear()}
        placeholder={t('common.year')}
        {...config}
      />
      <CloseButton onClick={onClear} />
    </div>
  );
}
